import React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  BusinessLine,
  ClientEngagement,
  CommercialBusinessUnit,
  DashboardParams,
  House,
} from "../../store/apis/commercialDashboardApi";
import {
  BUSINESS_LINE_LABEL,
  ENGAGEMENT_LABEL,
  HOUSE_NAME,
  PeriodPreset,
  periodRange,
  shortDate,
} from "./dashboardFormat";

/** El estado de los filtros, tal como lo maneja la pantalla. */
export interface FilterState {
  businessUnit: CommercialBusinessUnit | "ALL";
  period: PeriodPreset;
  /** Solo con periodo personalizado. YYYY-MM-DD. */
  from: string;
  to: string;
  businessLine: BusinessLine | "ALL";
  engagement: ClientEngagement | "ALL";
  house: House | "ALL";
  owner: string;
}

export const INITIAL_FILTERS: FilterState = {
  businessUnit: "ALL",
  period: "YEAR",
  ...periodRange("YEAR"),
  businessLine: "ALL",
  engagement: "ALL",
  house: "ALL",
  owner: "",
};

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Los filtros como parámetros de la API. Una fecha a medio escribir no se manda. */
export const toParams = (state: FilterState): DashboardParams => {
  const range =
    state.period === "CUSTOM"
      ? { from: state.from, to: state.to }
      : periodRange(state.period);
  return {
    businessUnit: state.businessUnit === "ALL" ? undefined : state.businessUnit,
    from: ISO.test(range.from) ? range.from : undefined,
    to: ISO.test(range.to) && range.to >= range.from ? range.to : undefined,
    businessLine: state.businessLine === "ALL" ? undefined : state.businessLine,
    engagement: state.engagement === "ALL" ? undefined : state.engagement,
    house: state.house === "ALL" ? undefined : state.house,
    owner: state.owner || undefined,
  };
};

const PERIOD_LABEL: Record<PeriodPreset, string> = {
  YEAR: "Año en curso",
  QUARTER: "Trimestre en curso",
  LAST_90: "Últimos 90 días",
  CUSTOM: "Personalizado",
};

/** El resumen de lo filtrado, debajo de la cabecera. */
export const filtersSummary = (state: FilterState): string => {
  const params = toParams(state);
  const parts = [
    params.businessUnit === "BEAPOLO"
      ? "BeApolo"
      : params.businessUnit === "HOUSE_GROUP"
        ? "House Group"
        : "House Group + BeApolo",
    `${PERIOD_LABEL[state.period]}${params.from ? ` (${shortDate(params.from)}${params.to ? ` – ${shortDate(params.to)}` : ""})` : ""}`,
  ];
  if (params.businessLine) parts.push(BUSINESS_LINE_LABEL[params.businessLine]);
  if (params.engagement) parts.push(ENGAGEMENT_LABEL[params.engagement]);
  if (params.house) parts.push(HOUSE_NAME[params.house]);
  if (params.owner) parts.push(params.owner);
  return parts.join(" · ");
};

interface Props {
  value: FilterState;
  onChange: (next: FilterState) => void;
  owners: string[];
  /** El filtro de negocio no aplica en el pipeline comercial, que es siempre NB. */
  showBusinessLine: boolean;
  /** Ni el de House: el pipeline comercial no tiene Houses. */
  showHouse: boolean;
}

/** Los filtros globales: se aplican a todo lo que está en pantalla. */
const DashboardFilters: React.FC<Props> = ({
  value,
  onChange,
  owners,
  showBusinessLine,
  showHouse,
}) => {
  const set = <K extends keyof FilterState>(key: K, next: FilterState[K]) =>
    onChange({ ...value, [key]: next });

  const select = (
    id: string,
    text: string,
    current: string,
    options: [string, string][],
    onSelect: (next: string) => void,
    minWidth = 150
  ) => (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={id}>{text}</InputLabel>
      <Select
        labelId={id}
        label={text}
        value={current}
        onChange={(event) => onSelect(String(event.target.value))}
      >
        {options.map(([optionValue, optionLabel]) => (
          <MenuItem key={optionValue} value={optionValue}>
            {optionLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Stack
      direction="row"
      gap={1.5}
      flexWrap="wrap"
      useFlexGap
      alignItems="center"
    >
      {select(
        "dashboard-business-unit",
        "Empresa",
        value.businessUnit,
        [
          ["ALL", "House Group + BeApolo"],
          ["HOUSE_GROUP", "House Group"],
          ["BEAPOLO", "BeApolo"],
        ],
        (next) => set("businessUnit", next as FilterState["businessUnit"]),
        190
      )}
      {select(
        "dashboard-period",
        "Periodo",
        value.period,
        (Object.keys(PERIOD_LABEL) as PeriodPreset[]).map((key) => [
          key,
          PERIOD_LABEL[key],
        ]),
        (next) => {
          const period = next as PeriodPreset;
          // Al pasar a personalizado se parte del periodo que se estaba viendo.
          onChange(
            period === "CUSTOM"
              ? { ...value, period, ...toRange(value) }
              : { ...value, period, ...periodRange(period) }
          );
        }
      )}
      {value.period === "CUSTOM" && (
        <>
          <TextField
            size="small"
            type="date"
            label="Desde"
            value={value.from}
            onChange={(event) => set("from", event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={value.to}
            onChange={(event) => set("to", event.target.value)}
            InputLabelProps={{ shrink: true }}
            error={Boolean(value.to && value.from && value.to < value.from)}
          />
        </>
      )}
      {showBusinessLine &&
        select(
          "dashboard-business-line",
          "Negocio",
          value.businessLine,
          [
            ["ALL", "NB + EB"],
            ["NB", "New Business"],
            ["EB", "Existing Business"],
          ],
          (next) => set("businessLine", next as FilterState["businessLine"])
        )}
      {select(
        "dashboard-engagement",
        "Tipo de cliente",
        value.engagement,
        [["ALL", "Todos"], ...Object.entries(ENGAGEMENT_LABEL)] as [
          string,
          string,
        ][],
        (next) => set("engagement", next as FilterState["engagement"])
      )}
      {showHouse &&
        select(
          "dashboard-house",
          "House",
          value.house,
          [
            ["ALL", "Todas"],
            ...Object.entries(HOUSE_NAME).filter(
              ([key]) => key !== "SIN_HOUSE"
            ),
          ] as [string, string][],
          (next) => set("house", next as FilterState["house"])
        )}
      {select(
        "dashboard-owner",
        "Owner",
        value.owner || "ALL",
        [["ALL", "Todos"], ...owners.map((owner) => [owner, owner])] as [
          string,
          string,
        ][],
        (next) => set("owner", next === "ALL" ? "" : next),
        170
      )}
    </Stack>
  );
};

const toRange = (state: FilterState) =>
  state.period === "CUSTOM"
    ? { from: state.from, to: state.to }
    : periodRange(state.period);

export const FiltersSummaryLine: React.FC<{ state: FilterState }> = ({
  state,
}) => (
  <Typography variant="caption" color="text.secondary">
    {filtersSummary(state)}
  </Typography>
);

export default DashboardFilters;
